import { useState, useEffect } from 'react';
import { 
  Search, Eye, Download, DollarSign, Shield, TrendingUp, 
  Wallet, CheckCircle, XCircle, Edit2, Save, X, Zap, Target, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import DepositAddressManager from '../../components/admin/DepositAddressManager';

import { fmtFixed, fmtNumber } from '../../utils/format';
const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterKYC, setFilterKYC] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Form states for different actions
  const [balanceForm, setBalanceForm] = useState({ amount: '', reason: '', type: 'add' });
  const [cryptoForm, setCryptoForm] = useState({ crypto: 'BTC', amount: '', type: 'add' });
  const [kycForm, setKycForm] = useState({ status: '', level: 1, feedback: '' });
  const [depositAddressForm, setDepositAddressForm] = useState({ crypto: 'BTC', address: '' });
  const [tradeOutcomeForm, setTradeOutcomeForm] = useState({ tradeId: '', outcome: 'win' });
  const [legacyReason, setLegacyReason] = useState('');
  const [legacyUpdating, setLegacyUpdating] = useState(false);

  // Pending requests for selected user
  const [pendingDeposits, setPendingDeposits] = useState([]);
  const [pendingWithdrawals, setPendingWithdrawals] = useState([]);
  const [activeTrades, setActiveTrades] = useState([]);

  // Crypto holdings state
  const [userHoldings, setUserHoldings] = useState(null);
  const [loadingHoldings, setLoadingHoldings] = useState(false);
  const [editingCrypto, setEditingCrypto] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  // Trade settings state
  const [tradeSettings, setTradeSettings] = useState({
    trade_mode: 'normal',
    win_rate: 50,
    apply_to: 'all',
    reason: ''
  });
  const [loadingTradeSettings, setLoadingTradeSettings] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      // Use limit=10000 to fetch all users (default backend limit is 100, hiding migrated users)
      const response = await api.get('/admin/users?limit=10000', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUsers(response.data?.users || response.data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('adminToken');
      
      // Fetch pending deposits
      const depositsRes = await api.get('/admin/deposits/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPendingDeposits(depositsRes.data.filter(d => d.user_id === userId));

      // Fetch pending withdrawals
      const withdrawalsRes = await api.get('/admin/withdrawals/pending', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setPendingWithdrawals(withdrawalsRes.data.filter(w => w.user_id === userId));

      // Fetch active trades
      const tradesRes = await api.get(`/admin/trades/user/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setActiveTrades(tradesRes.data || []);
    } catch (error) {
      console.error('Error fetching user details:', error);
    }
  };

  const fetchUserHoldings = async (userId) => {
    try {
      setLoadingHoldings(true);
      const token = localStorage.getItem('adminToken');
      const response = await api.get(`/admin/wallet/user/${userId}/holdings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserHoldings(response.data);
    } catch (error) {
      console.error('Error fetching user holdings:', error);
      toast.error('Failed to fetch crypto holdings');
    } finally {
      setLoadingHoldings(false);
    }
  };

  const fetchTradeSettings = async (userId) => {
    try {
      setLoadingTradeSettings(true);
      const token = localStorage.getItem('adminToken');
      const response = await api.get(`/admin/user/${userId}/trade-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const settings = response.data.trade_settings || {
        trade_mode: 'normal',
        win_rate: 50,
        apply_to: 'all',
        reason: ''
      };
      setTradeSettings(settings);
    } catch (error) {
      console.error('Error fetching trade settings:', error);
      setTradeSettings({ trade_mode: 'normal', win_rate: 50, apply_to: 'all', reason: '' });
    } finally {
      setLoadingTradeSettings(false);
    }
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setKycForm({ status: user.kyc_status, level: user.kyc_level || 1, feedback: '' });
    fetchUserDetails(user.user_id);
    fetchUserHoldings(user.user_id);
    fetchTradeSettings(user.user_id);
    setShowUserModal(true);
  };

  const handleBalanceUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      const amount = parseFloat(balanceForm.amount);
      
      if (isNaN(amount) || amount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      
      const finalAmount = balanceForm.type === 'subtract' ? -amount : amount;

      await api.post('/admin/balance/adjust', {
        user_id: selectedUser.user_id,
        amount: finalAmount,
        reason: balanceForm.reason || 'Admin adjustment',
        adjustment_type: balanceForm.type
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Balance updated successfully');
      setBalanceForm({ amount: '', reason: '', type: 'add' });
      fetchUsers();
      
      // Update selected user
      const updatedUser = { ...selectedUser, balance: (selectedUser.balance || 0) + finalAmount };
      setSelectedUser(updatedUser);
    } catch (error) {
      console.error('Error updating balance:', error);
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to update balance')
        : 'Failed to update balance';
      toast.error(errorMessage);
    }
  };

  const handleCryptoUpdate = async (cryptoSymbol, newAmount, operation, reason) => {
    try {
      const token = localStorage.getItem('adminToken');
      const amount = parseFloat(newAmount);
      
      if (isNaN(amount) || amount < 0) {
        toast.error('Please enter a valid amount');
        return;
      }

      await api.put(`/admin/wallet/user/${selectedUser.user_id}/crypto`, {
        crypto_symbol: cryptoSymbol,
        amount: amount,
        operation: operation,
        reason: reason || 'Admin adjustment'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(`${cryptoSymbol} holdings updated successfully`);
      setEditingCrypto(null);
      setEditAmount('');
      
      // Refresh holdings and user list
      await fetchUserHoldings(selectedUser.user_id);
      await fetchUsers();
      
      // Update selected user balance
      const updatedHoldings = await api.get(`/admin/wallet/user/${selectedUser.user_id}/holdings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSelectedUser(prev => ({
        ...prev,
        balance: updatedHoldings.data.total_balance_usdt
      }));
    } catch (error) {
      console.error('Error updating crypto:', error);
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to update crypto holdings')
        : 'Failed to update crypto holdings';
      toast.error(errorMessage);
    }
  };

  const handleQuickAddCrypto = async (e) => {
    e.preventDefault();
    await handleCryptoUpdate(
      cryptoForm.crypto, 
      cryptoForm.amount, 
      cryptoForm.type === 'add' ? 'add' : 'subtract',
      `Quick ${cryptoForm.type} via admin panel`
    );
    setCryptoForm({ crypto: 'BTC', amount: '', type: 'add' });
  };

  const handleKYCUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      
      if (!kycForm.status) {
        toast.error('Please select a KYC status');
        return;
      }
      
      await api.put(`/admin/kyc/${selectedUser.user_id}/review`, {
        kyc_status: kycForm.status,
        kyc_level: parseInt(kycForm.level),
        feedback: kycForm.feedback
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('KYC status updated successfully');
      fetchUsers();
      
      // Update selected user
      const updatedUser = { 
        ...selectedUser, 
        kyc_status: kycForm.status, 
        kyc_level: parseInt(kycForm.level) 
      };
      setSelectedUser(updatedUser);
    } catch (error) {
      console.error('Error updating KYC:', error);
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to update KYC status')
        : 'Failed to update KYC status';
      toast.error(errorMessage);
    }
  };

  const handleDepositApproval = async (depositId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/deposits/${depositId}/approve`, {
        deposit_id: depositId,
        status: status,
        admin_notes: status === 'approved' ? 'Approved from user management' : 'Rejected'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(`Deposit ${status} successfully`);
      fetchUserDetails(selectedUser.user_id);
      fetchUsers();
    } catch (error) {
      console.error('Error processing deposit:', error);
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to process deposit')
        : 'Failed to process deposit';
      toast.error(errorMessage);
    }
  };

  const handleWithdrawalApproval = async (withdrawalId, status) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(`/admin/withdrawals/${withdrawalId}/process`, {
        withdrawal_id: withdrawalId,
        status: status,
        admin_notes: status === 'approved' ? 'Approved from user management' : 'Rejected'
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(`Withdrawal ${status} successfully`);
      fetchUserDetails(selectedUser.user_id);
      fetchUsers();
    } catch (error) {
      console.error('Error processing withdrawal:', error);
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to process withdrawal')
        : 'Failed to process withdrawal';
      toast.error(errorMessage);
    }
  };

  const handleTradeOutcomeUpdate = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }    
    try {
      const token = localStorage.getItem('adminToken');
      
      // Use the correct backend endpoint for binary trades
      await api.post('/admin/binary-trades/control-outcome', {
        trade_id: tradeOutcomeForm.tradeId,
        outcome: tradeOutcomeForm.outcome
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Trade outcome updated successfully');
      setTradeOutcomeForm({ tradeId: '', outcome: 'win' });
      fetchUserDetails(selectedUser.user_id);
    } catch (error) {
      console.error('Error updating trade:', error);
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : 'Failed to update trade outcome')
        : 'Failed to update trade outcome';
      toast.error(errorMessage);
    }
  };

  const handleTradeSettingsUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      
      await api.put(`/admin/user/${selectedUser.user_id}/trade-settings`, {
        user_id: selectedUser.user_id,
        trade_mode: tradeSettings.trade_mode,
        win_rate: tradeSettings.trade_mode === 'custom' ? parseInt(tradeSettings.win_rate) : null,
        apply_to: tradeSettings.apply_to,
        reason: tradeSettings.reason
      }, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success('Trade settings updated successfully');
    } catch (error) {
      console.error('Error updating trade settings:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update trade settings';
      toast.error(errorMessage);
    }
  };

  const handleLegacyAuthToggle = async (nextEnabled) => {
    if (!selectedUser) return;
    if (nextEnabled && !window.confirm(`Enable legacy password bypass for ${selectedUser.email}? They will be able to log in with ANY password.`)) {
      return;
    }
    try {
      setLegacyUpdating(true);
      const token = localStorage.getItem('adminToken');
      const response = await api.put(
        `/admin/users/${selectedUser.user_id}/legacy-auth`,
        { enabled: nextEnabled, reason: legacyReason || '' },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      toast.success(response.data?.message || 'Legacy auth updated');
      setSelectedUser({ ...selectedUser, is_legacy_auth: nextEnabled });
      setUsers((prev) => prev.map((u) => (u.user_id === selectedUser.user_id ? { ...u, is_legacy_auth: nextEnabled } : u)));
      setLegacyReason('');
    } catch (error) {
      console.error('Error toggling legacy auth:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to update legacy auth';
      toast.error(typeof errorMessage === 'string' ? errorMessage : 'Failed to update legacy auth');
    } finally {
      setLegacyUpdating(false);
    }
  };


  const handleResolveAllTrades = async (outcome) => {
    if (!window.confirm(`Are you sure you want to set ALL active trades for this user to ${outcome.toUpperCase()}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('adminToken');
      
      const response = await api.post(`/admin/user/${selectedUser.user_id}/resolve-all-trades?outcome=${outcome}`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      toast.success(`Resolved ${response.data.resolved_trades} trades and ${response.data.resolved_investments} investments`);
      fetchUserDetails(selectedUser.user_id);
      fetchUsers();
    } catch (error) {
      console.error('Error resolving trades:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to resolve trades';
      toast.error(errorMessage);
    }
  };

  const handleExportCSV = () => {
    try {
      if (!filteredUsers.length) {
        toast.error('No users to export');
        return;
      }
      const headers = [
        'User ID', 'Name', 'Email', 'Balance (USDT)',
        'KYC Status', 'KYC Level', 'Account Status', 'Joined'
      ];
      const escape = (v) => {
        const s = v === null || v === undefined ? '' : String(v);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = filteredUsers.map(u => [
        u.user_id || u.id || '',
        u.name || '',
        u.email || '',
        fmtFixed((u.balance || 0), 2),
        u.kyc_status || 'not_submitted',
        u.kyc_level || 0,
        u.account_status || 'active',
        u.created_at ? new Date(u.created_at).toISOString() : ''
      ].map(escape).join(','));
      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `phantom-trades-users-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`Exported ${filteredUsers.length} users to CSV`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || user.account_status === filterStatus;
    const matchesKYC = filterKYC === 'all' || user.kyc_status === filterKYC;
    return matchesSearch && matchesStatus && matchesKYC;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'suspended': return 'bg-red-500/20 text-red-400';
      case 'banned': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-yellow-500/20 text-yellow-400';
    }
  };

  const getKYCColor = (status) => {
    switch(status) {
      case 'approved': return 'bg-green-500/20 text-green-400';
      case 'pending': return 'bg-yellow-500/20 text-yellow-400';
      case 'rejected': return 'bg-red-500/20 text-red-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Users Management</h1>
          <p className="text-gray-400 text-sm">Manage all user accounts and actions</p>
        </div>
        <button
          onClick={handleExportCSV}
          data-testid="export-csv-btn"
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white font-semibold text-sm"
        >
          <Download className="h-4 w-4" />
          <span>Export CSV</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative md:col-span-2">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by email or name..."
              className="w-full pl-10 pr-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="banned">Banned</option>
          </select>
          <select
            value={filterKYC}
            onChange={(e) => setFilterKYC(e.target.value)}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
          >
            <option value="all">All KYC</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
            <option value="not_submitted">Not Submitted</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-gray-400 text-xs mb-1">Total Users</p>
          <p className="text-white text-2xl font-bold">{users.length}</p>
        </div>
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-gray-400 text-xs mb-1">Active</p>
          <p className="text-green-400 text-2xl font-bold">
            {users.filter(u => u.account_status === 'active').length}
          </p>
        </div>
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-gray-400 text-xs mb-1">KYC Approved</p>
          <p className="text-blue-400 text-2xl font-bold">
            {users.filter(u => u.kyc_status === 'approved').length}
          </p>
        </div>
        <div className="bg-gray-900 rounded-xl p-4 border border-gray-800">
          <p className="text-gray-400 text-xs mb-1">Pending KYC</p>
          <p className="text-yellow-400 text-2xl font-bold">
            {users.filter(u => u.kyc_status === 'pending').length}
          </p>
        </div>
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">User</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Balance</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">KYC</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-bold text-gray-400 uppercase">Joined</th>
                    <th className="px-6 py-3 text-right text-xs font-bold text-gray-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredUsers.map((user) => (
                    <tr key={user.user_id} className={`hover:bg-gray-800/50 ${user.is_test_user ? 'opacity-60' : ''}`}>
                      <td className="px-6 py-4">
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-semibold text-sm">{user.name || 'N/A'}</p>
                            {user.is_test_user && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-700 text-gray-300" data-testid="test-user-badge">TEST</span>
                            )}
                            {user.is_legacy_auth && (
                              <span className="px-2 py-0.5 rounded text-[10px] font-semibold bg-amber-500/20 text-amber-300">LEGACY</span>
                            )}
                          </div>
                          <p className="text-gray-400 text-xs">{user.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-white font-bold">{fmtFixed((user.balance || 0), 2)} USDT</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getKYCColor(user.kyc_status)}`}>
                          {user.kyc_status || 'not_submitted'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(user.account_status)}`}>
                          {user.account_status || 'active'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-400 text-sm">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                        </p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm font-semibold flex items-center space-x-1"
                          >
                            <Eye className="h-4 w-4" />
                            <span>Manage</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile View */}
          <div className="md:hidden space-y-3">
            {filteredUsers.map((user) => (
              <div key={user.user_id} className="bg-gray-900 rounded-xl p-4 border border-gray-800">
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-white font-bold text-sm">{user.name || 'N/A'}</p>
                    <p className="text-gray-400 text-xs">{user.email}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getKYCColor(user.kyc_status)}`}>
                    {user.kyc_status || 'not_submitted'}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <p className="text-gray-400 text-xs">Balance:</p>
                  <p className="text-white font-bold">{fmtFixed((user.balance || 0), 2)} USDT</p>
                </div>
                <button
                  onClick={() => handleViewUser(user)}
                  className="w-full px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm font-semibold flex items-center justify-center space-x-1"
                >
                  <Eye className="h-4 w-4" />
                  <span>Manage User</span>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* User Management Modal */}
      {showUserModal && selectedUser && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gray-900 rounded-xl max-w-6xl w-full my-8 border border-gray-800">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900 z-10">
              <div>
                <h3 className="text-xl font-bold text-white">{selectedUser.name || 'User'}</h3>
                <p className="text-gray-400 text-sm">{selectedUser.email}</p>
              </div>
              <button
                onClick={() => setShowUserModal(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* User Info Cards */}
            <div className="p-6 border-b border-gray-800">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-400 text-xs mb-1">Balance</p>
                  <p className="text-white text-xl font-bold">{fmtFixed((selectedUser.balance || 0), 2)} USDT</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-400 text-xs mb-1">KYC Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getKYCColor(selectedUser.kyc_status)}`}>
                    {selectedUser.kyc_status || 'not_submitted'}
                  </span>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-400 text-xs mb-1">KYC Level</p>
                  <p className="text-white text-xl font-bold">Level {selectedUser.kyc_level || 0}</p>
                </div>
                <div className="bg-gray-800 rounded-lg p-4">
                  <p className="text-gray-400 text-xs mb-1">Status</p>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedUser.account_status)}`}>
                    {selectedUser.account_status || 'active'}
                  </span>
                  {selectedUser.is_legacy_auth && (
                    <div className="mt-2">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-300">
                        <KeyRound className="h-3 w-3" /> Legacy Auth
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-gray-800 overflow-x-auto">
              <div className="flex space-x-1 p-2">
                {[
                  { id: 'balance', label: 'Balance', icon: DollarSign },
                  { id: 'crypto', label: 'Crypto', icon: Wallet },
                  { id: 'trade-control', label: 'Trade Control', icon: Target },
                  { id: 'kyc', label: 'KYC', icon: Shield },
                  { id: 'legacy-auth', label: 'Legacy Auth', icon: KeyRound },
                  { id: 'addresses', label: 'Addresses', icon: Wallet },
                  { id: 'deposits', label: 'Deposits', icon: CheckCircle },
                  { id: 'withdrawals', label: 'Withdrawals', icon: XCircle },
                  { id: 'trades', label: 'Trades', icon: TrendingUp },
                ].map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-semibold text-sm whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-purple-600 text-white'
                          : 'text-gray-400 hover:text-white hover:bg-gray-800'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {/* Balance Tab */}
              {activeTab === 'balance' && (
                <form onSubmit={handleBalanceUpdate} className="space-y-4">
                  <h4 className="text-white font-bold text-lg mb-4">Update User Balance</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white font-semibold mb-2 block text-sm">Action</label>
                      <select
                        value={balanceForm.type}
                        onChange={(e) => setBalanceForm({ ...balanceForm, type: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="add">Add Balance</option>
                        <option value="subtract">Subtract Balance</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-white font-semibold mb-2 block text-sm">Amount (USDT)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={balanceForm.amount}
                        onChange={(e) => setBalanceForm({ ...balanceForm, amount: e.target.value })}
                        placeholder="0.00"
                        className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-white font-semibold mb-2 block text-sm">Reason</label>
                    <textarea
                      value={balanceForm.reason}
                      onChange={(e) => setBalanceForm({ ...balanceForm, reason: e.target.value })}
                      placeholder="Reason for adjustment..."
                      className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-24"
                    />
                  </div>
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <p className="text-sm text-gray-300">
                      Current Balance: <span className="font-bold text-white">{fmtFixed((selectedUser.balance || 0), 2)} USDT</span>
                      {balanceForm.amount && (
                        <>
                          {' → New Balance: '}
                          <span className="font-bold text-white">
                            {fmtFixed(((selectedUser.balance || 0) + (balanceForm.type === 'add' ? parseFloat(balanceForm.amount || 0) : -parseFloat(balanceForm.amount || 0))), 2)} USDT
                          </span>
                        </>
                      )}
                    </p>
                  </div>
                  <button
                    type="submit"
                    className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2"
                  >
                    <Save className="h-5 w-5" />
                    <span>Update Balance</span>
                  </button>
                </form>
              )}

              {/* Crypto Holdings Tab */}
              {activeTab === 'crypto' && (
                <div className="space-y-6">
                  {/* Total Balance Summary */}
                  {userHoldings && (
                    <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl p-6">
                      <p className="text-purple-200 text-sm mb-2">Total Portfolio Value</p>
                      <p className="text-white text-3xl font-bold">{(userHoldings.total_balance_usdt != null ? fmtFixed(userHoldings.total_balance_usdt, 2) : undefined) || '0.00'} USDT</p>
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-purple-200 text-xs">Available</p>
                          <p className="text-white text-lg font-semibold">{(userHoldings.total_available_usdt != null ? fmtFixed(userHoldings.total_available_usdt, 2) : undefined) || '0.00'} USDT</p>
                        </div>
                        <div>
                          <p className="text-purple-200 text-xs">Locked</p>
                          <p className="text-white text-lg font-semibold">{(userHoldings.total_locked_usdt != null ? fmtFixed(userHoldings.total_locked_usdt, 2) : undefined) || '0.00'} USDT</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Current Holdings */}
                  <div>
                    <h4 className="text-white font-bold text-lg mb-4">Current Holdings</h4>
                    {loadingHoldings ? (
                      <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-4 border-purple-500 border-t-transparent"></div>
                      </div>
                    ) : userHoldings && userHoldings.holdings && userHoldings.holdings.length > 0 ? (
                      <div className="space-y-3">
                        {userHoldings.holdings.map((holding) => (
                          <div key={holding.crypto_symbol} className="bg-gray-800 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex-1">
                                <div className="flex items-center space-x-3">
                                  <div>
                                    <p className="text-white font-bold text-lg">{holding.crypto_name}</p>
                                    <p className="text-gray-400 text-xs">{holding.crypto_symbol}</p>
                                  </div>
                                </div>
                                <div className="mt-2 grid grid-cols-2 gap-3">
                                  <div>
                                    <p className="text-gray-400 text-xs">Balance</p>
                                    <p className="text-white font-semibold">{(holding.balance != null ? fmtFixed(holding.balance, 8) : undefined) || '0.00000000'} {holding.crypto_symbol}</p>
                                  </div>
                                  <div>
                                    <p className="text-gray-400 text-xs">USDT Value</p>
                                    <p className="text-white font-semibold">{(holding.usdt_value != null ? fmtFixed(holding.usdt_value, 2) : undefined) || '0.00'} USDT</p>
                                  </div>
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setEditingCrypto(holding.crypto_symbol);
                                  setEditAmount(holding.balance?.toString() || '0');
                                }}
                                className="ml-4 p-2 bg-purple-600 hover:bg-purple-700 rounded-lg"
                              >
                                <Edit2 className="h-4 w-4 text-white" />
                              </button>
                            </div>

                            {/* Inline Edit Form */}
                            {editingCrypto === holding.crypto_symbol && (
                              <div className="mt-3 pt-3 border-t border-gray-700 space-y-3">
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                  <div>
                                    <label className="text-gray-400 text-xs mb-1 block">New Amount</label>
                                    <input
                                      type="number"
                                      step="0.00000001"
                                      value={editAmount}
                                      onChange={(e) => setEditAmount(e.target.value)}
                                      className="w-full px-3 py-2 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                    />
                                  </div>
                                  <div className="sm:col-span-2">
                                    <label className="text-gray-400 text-xs mb-1 block">Action</label>
                                    <div className="flex space-x-2">
                                      <button
                                        onClick={() => handleCryptoUpdate(holding.crypto_symbol, editAmount, 'set', 'Set balance via admin panel')}
                                        className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-sm font-semibold"
                                      >
                                        Set Amount
                                      </button>
                                      <button
                                        onClick={() => {
                                          setEditingCrypto(null);
                                          setEditAmount('');
                                        }}
                                        className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-sm"
                                      >
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="bg-gray-800 rounded-lg p-8 text-center">
                        <Wallet className="h-12 w-12 text-gray-600 mx-auto mb-3" />
                        <p className="text-gray-400">No crypto holdings found</p>
                      </div>
                    )}
                  </div>

                  {/* Quick Add Form */}
                  <div className="border-t border-gray-700 pt-6">
                    <h4 className="text-white font-bold text-lg mb-4">Quick Add/Subtract</h4>
                    <form onSubmit={handleQuickAddCrypto} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <div>
                          <label className="text-white font-semibold mb-2 block text-sm">Cryptocurrency</label>
                          <select
                            value={cryptoForm.crypto}
                            onChange={(e) => setCryptoForm({ ...cryptoForm, crypto: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="BTC">Bitcoin (BTC)</option>
                            <option value="ETH">Ethereum (ETH)</option>
                            <option value="USDT">Tether (USDT)</option>
                            <option value="BNB">Binance Coin (BNB)</option>
                            <option value="SOL">Solana (SOL)</option>
                            <option value="XRP">Ripple (XRP)</option>
                            <option value="ADA">Cardano (ADA)</option>
                            <option value="DOT">Polkadot (DOT)</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-white font-semibold mb-2 block text-sm">Action</label>
                          <select
                            value={cryptoForm.type}
                            onChange={(e) => setCryptoForm({ ...cryptoForm, type: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                          >
                            <option value="add">Add</option>
                            <option value="subtract">Subtract</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-white font-semibold mb-2 block text-sm">Amount</label>
                          <input
                            type="number"
                            step="0.00000001"
                            value={cryptoForm.amount}
                            onChange={(e) => setCryptoForm({ ...cryptoForm, amount: e.target.value })}
                            placeholder="0.00000000"
                            className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                            required
                          />
                        </div>
                      </div>
                      <button
                        type="submit"
                        className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2"
                      >
                        <Save className="h-5 w-5" />
                        <span>{cryptoForm.type === 'add' ? 'Add' : 'Subtract'} {cryptoForm.crypto}</span>
                      </button>
                    </form>
                  </div>
                </div>
              )}

              {/* Trade Control Tab */}
              {activeTab === 'trade-control' && (
                <div className="space-y-6">
                  {/* Current Trade Settings */}
                  <div className="bg-gradient-to-r from-yellow-600 to-orange-600 rounded-xl p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <Target className="h-8 w-8 text-white" />
                      <div>
                        <h4 className="text-white font-bold text-xl">Trade Outcome Control</h4>
                        <p className="text-yellow-100 text-sm">Control whether this user wins or loses trades</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white/10 rounded-lg p-4">
                        <p className="text-yellow-100 text-xs mb-1">Current Mode</p>
                        <p className="text-white text-lg font-bold capitalize">
                          {tradeSettings.trade_mode === 'always_win' ? '✅ Always Win' :
                           tradeSettings.trade_mode === 'always_lose' ? '❌ Always Lose' :
                           tradeSettings.trade_mode === 'custom' ? `🎯 Custom (${tradeSettings.win_rate}% Win)` :
                           '⚖️ Normal'}
                        </p>
                      </div>
                      <div className="bg-white/10 rounded-lg p-4">
                        <p className="text-yellow-100 text-xs mb-1">Applies To</p>
                        <p className="text-white text-lg font-bold capitalize">{tradeSettings.apply_to || 'All'} Trades</p>
                      </div>
                    </div>
                  </div>

                  {/* Quick Actions - Resolve All Trades */}
                  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
                    <h4 className="text-white font-bold text-lg mb-4 flex items-center space-x-2">
                      <Zap className="h-5 w-5 text-yellow-400" />
                      <span>Quick Actions - Resolve All Active Trades</span>
                    </h4>
                    <p className="text-gray-400 text-sm mb-4">
                      Immediately resolve ALL active trades for this user with a single click.
                      This will settle all binary trades and arbitrage investments.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() => handleResolveAllTrades('win')}
                        className="px-6 py-4 bg-green-600 hover:bg-green-700 rounded-xl text-white font-bold text-lg flex items-center justify-center space-x-2 transition-all"
                      >
                        <CheckCircle className="h-6 w-6" />
                        <span>SET ALL TO WIN</span>
                      </button>
                      <button
                        onClick={() => handleResolveAllTrades('lose')}
                        className="px-6 py-4 bg-red-600 hover:bg-red-700 rounded-xl text-white font-bold text-lg flex items-center justify-center space-x-2 transition-all"
                      >
                        <XCircle className="h-6 w-6" />
                        <span>SET ALL TO LOSE</span>
                      </button>
                    </div>
                  </div>

                  {/* Trade Settings Form */}
                  <form onSubmit={handleTradeSettingsUpdate} className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-4">
                    <h4 className="text-white font-bold text-lg mb-2">Future Trade Settings</h4>
                    <p className="text-gray-400 text-sm mb-4">
                      Configure how future trades will be resolved for this user.
                    </p>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-white font-semibold mb-2 block text-sm">Trade Mode</label>
                        <select
                          value={tradeSettings.trade_mode}
                          onChange={(e) => setTradeSettings({ ...tradeSettings, trade_mode: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="normal">⚖️ Normal (Market-based)</option>
                          <option value="always_win">✅ Always Win</option>
                          <option value="always_lose">❌ Always Lose</option>
                          <option value="custom">🎯 Custom Win Rate</option>
                        </select>
                      </div>
                      
                      {tradeSettings.trade_mode === 'custom' && (
                        <div>
                          <label className="text-white font-semibold mb-2 block text-sm">Win Rate (%)</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={tradeSettings.win_rate}
                            onChange={(e) => setTradeSettings({ ...tradeSettings, win_rate: e.target.value })}
                            className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                            placeholder="50"
                          />
                        </div>
                      )}
                      
                      <div>
                        <label className="text-white font-semibold mb-2 block text-sm">Apply To</label>
                        <select
                          value={tradeSettings.apply_to}
                          onChange={(e) => setTradeSettings({ ...tradeSettings, apply_to: e.target.value })}
                          className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                        >
                          <option value="all">All Trades (Binary + Arbitrage)</option>
                          <option value="binary">Binary Trades Only</option>
                          <option value="arbitrage">Arbitrage Only</option>
                        </select>
                      </div>
                    </div>
                    
                    <div>
                      <label className="text-white font-semibold mb-2 block text-sm">Reason/Notes</label>
                      <textarea
                        value={tradeSettings.reason || ''}
                        onChange={(e) => setTradeSettings({ ...tradeSettings, reason: e.target.value })}
                        placeholder="Optional reason for this setting..."
                        className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 h-20"
                      />
                    </div>

                    <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                      <p className="text-yellow-400 text-sm">
                        <strong>⚠️ Warning:</strong> These settings will affect all future trades made by this user. 
                        The settings are applied when trades are settled, not when they are placed.
                      </p>
                    </div>
                    
                    <button
                      type="submit"
                      className="w-full px-4 py-3 bg-yellow-600 hover:bg-yellow-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2"
                    >
                      <Save className="h-5 w-5" />
                      <span>Save Trade Settings</span>
                    </button>
                  </form>
                </div>
              )}

              {/* Deposit Addresses Tab */}
              {activeTab === 'addresses' && (
                <div>
                  <DepositAddressManager 
                    userId={selectedUser.user_id}
                    userEmail={selectedUser.email}
                  />
                </div>
              )}

              {/* KYC Tab */}
              {activeTab === 'legacy-auth' && (
                <div className="space-y-6" data-testid="legacy-auth-tab">
                  <div className={`rounded-xl p-6 border ${selectedUser.is_legacy_auth ? 'bg-amber-500/10 border-amber-500/40' : 'bg-gray-800 border-gray-700'}`}>
                    <div className="flex items-start space-x-3">
                      <KeyRound className={`h-8 w-8 ${selectedUser.is_legacy_auth ? 'text-amber-300' : 'text-gray-400'}`} />
                      <div className="flex-1">
                        <h4 className="text-white font-bold text-lg">Legacy Password Bypass</h4>
                        <p className="text-gray-300 text-sm mt-1">
                          When enabled, this user can log in with <strong>any password</strong>. Every legacy
                          login is recorded in the <code className="text-amber-300">legacy_login_audit</code> collection.
                          Use only for grandfathered accounts migrated from a prior system.
                        </p>
                        <p className="text-sm mt-3">
                          Current state:{' '}
                          <span className={`font-bold ${selectedUser.is_legacy_auth ? 'text-amber-300' : 'text-gray-200'}`}>
                            {selectedUser.is_legacy_auth ? 'ENABLED (any password works)' : 'DISABLED (normal auth)'}
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 space-y-4">
                    <div>
                      <label className="text-white font-semibold mb-2 block text-sm">Reason / Audit Note</label>
                      <textarea
                        value={legacyReason}
                        onChange={(e) => setLegacyReason(e.target.value)}
                        placeholder="e.g. Migrated user requested legacy access on 2026-02-14"
                        className="w-full px-4 py-3 bg-gray-900 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 h-20"
                        data-testid="legacy-auth-reason"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        disabled={legacyUpdating || selectedUser.is_legacy_auth}
                        onClick={() => handleLegacyAuthToggle(true)}
                        className="px-4 py-3 bg-amber-500 hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-black font-bold flex items-center justify-center space-x-2"
                        data-testid="enable-legacy-auth-btn"
                      >
                        <KeyRound className="h-5 w-5" />
                        <span>Enable Legacy Bypass</span>
                      </button>
                      <button
                        type="button"
                        disabled={legacyUpdating || !selectedUser.is_legacy_auth}
                        onClick={() => handleLegacyAuthToggle(false)}
                        className="px-4 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg text-white font-bold flex items-center justify-center space-x-2"
                        data-testid="disable-legacy-auth-btn"
                      >
                        <XCircle className="h-5 w-5" />
                        <span>Disable Legacy Bypass</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* KYC Tab */}
              {activeTab === 'kyc' && (
                <form onSubmit={handleKYCUpdate} className="space-y-4">
                  <h4 className="text-white font-bold text-lg mb-4">Update KYC Status</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="text-white font-semibold mb-2 block text-sm">KYC Status</label>
                      <select
                        value={kycForm.status}
                        onChange={(e) => setKycForm({ ...kycForm, status: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="approved">Approved</option>
                        <option value="rejected">Rejected</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-white font-semibold mb-2 block text-sm">KYC Level</label>
                      <select
                        value={kycForm.level}
                        onChange={(e) => setKycForm({ ...kycForm, level: e.target.value })}
                        className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value={1}>Level 1 - Basic ($1,000/day)</option>
                        <option value={2}>Level 2 - Advanced ($10,000/day)</option>
                        <option value={3}>Level 3 - VIP (Unlimited)</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-white font-semibold mb-2 block text-sm">Feedback/Notes</label>
                    <textarea
                      value={kycForm.feedback}
                      onChange={(e) => setKycForm({ ...kycForm, feedback: e.target.value })}
                      placeholder="Optional feedback for the user..."
                      className="w-full px-4 py-3 bg-gray-800 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 h-24"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full px-4 py-3 bg-green-600 hover:bg-green-700 rounded-lg text-white font-semibold flex items-center justify-center space-x-2"
                  >
                    <Save className="h-5 w-5" />
                    <span>Update KYC Status</span>
                  </button>
                </form>
              )}

              {/* Deposits Tab */}
              {activeTab === 'deposits' && (
                <div className="space-y-4">
                  <h4 className="text-white font-bold text-lg mb-4">
                    Pending Deposits ({pendingDeposits.length})
                  </h4>
                  {pendingDeposits.length === 0 ? (
                    <div className="bg-gray-800 rounded-lg p-8 text-center">
                      <p className="text-gray-400">No pending deposits</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pendingDeposits.map((deposit) => (
                        <div key={deposit.id} className="bg-gray-800 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-white font-bold text-lg">${fmtFixed(deposit.amount, 2)}</p>
                              <p className="text-gray-400 text-sm">{deposit.crypto_type} - {deposit.network}</p>
                              <p className="text-gray-400 text-xs mt-1">
                                {new Date(deposit.created_at).toLocaleString()}
                              </p>
                            </div>
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-400">
                              Pending
                            </span>
                          </div>
                          {deposit.transaction_hash && (
                            <p className="text-gray-400 text-xs mb-3 font-mono break-all">
                              TX: {deposit.transaction_hash}
                            </p>
                          )}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDepositApproval(deposit.id, 'approved')}
                              className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-semibold flex items-center justify-center space-x-1"
                            >
                              <CheckCircle className="h-4 w-4" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => handleDepositApproval(deposit.id, 'rejected')}
                              className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-semibold flex items-center justify-center space-x-1"
                            >
                              <XCircle className="h-4 w-4" />
                              <span>Reject</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Withdrawals Tab */}
              {activeTab === 'withdrawals' && (
                <div className="space-y-4">
                  <h4 className="text-white font-bold text-lg mb-4">
                    Pending Withdrawals ({pendingWithdrawals.length})
                  </h4>
                  {pendingWithdrawals.length === 0 ? (
                    <div className="bg-gray-800 rounded-lg p-8 text-center">
                      <p className="text-gray-400">No pending withdrawals</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {pendingWithdrawals.map((withdrawal) => (
                        <div key={withdrawal.id} className="bg-gray-800 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-white font-bold text-lg">${fmtFixed(withdrawal.amount, 2)}</p>
                              <p className="text-gray-400 text-sm">{withdrawal.crypto_type} - {withdrawal.network}</p>
                              <p className="text-gray-400 text-xs mt-1">
                                {new Date(withdrawal.created_at).toLocaleString()}
                              </p>
                            </div>
                            <span className="px-2 py-1 rounded-full text-xs font-semibold bg-yellow-500/20 text-yellow-400">
                              Pending
                            </span>
                          </div>
                          {withdrawal.wallet_address && (
                            <p className="text-gray-400 text-xs mb-3 font-mono break-all">
                              To: {withdrawal.wallet_address}
                            </p>
                          )}
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleWithdrawalApproval(withdrawal.id, 'approved')}
                              className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-semibold flex items-center justify-center space-x-1"
                            >
                              <CheckCircle className="h-4 w-4" />
                              <span>Approve</span>
                            </button>
                            <button
                              onClick={() => handleWithdrawalApproval(withdrawal.id, 'rejected')}
                              className="flex-1 px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-semibold flex items-center justify-center space-x-1"
                            >
                              <XCircle className="h-4 w-4" />
                              <span>Reject</span>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Trades Tab */}
              {activeTab === 'trades' && (
                <div className="space-y-4">
                  <h4 className="text-white font-bold text-lg mb-4">
                    Active Trades ({activeTrades.length})
                  </h4>
                  {activeTrades.length === 0 ? (
                    <div className="bg-gray-800 rounded-lg p-8 text-center">
                      <p className="text-gray-400">No active trades</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {activeTrades.map((trade) => (
                        <div key={trade.trade_id} className="bg-gray-800 rounded-lg p-4">
                          <div className="flex justify-between items-start mb-3">
                            <div>
                              <p className="text-white font-bold">{trade.trade_type}</p>
                              <p className="text-gray-400 text-sm">
                                {trade.trading_pair} - ${fmtNumber(trade.amount, 2)}
                              </p>
                              <p className="text-gray-400 text-xs mt-1">
                                Created: {new Date(trade.created_at).toLocaleString()}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              trade.status === 'active' ? 'bg-blue-500/20 text-blue-400' : 'bg-gray-500/20 text-gray-400'
                            }`}>
                              {trade.status}
                            </span>
                          </div>
                          <form onSubmit={handleTradeOutcomeUpdate} className="space-y-3">
                            <div className="grid grid-cols-2 gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setTradeOutcomeForm({ tradeId: trade.trade_id, outcome: 'win' });
                                  handleTradeOutcomeUpdate({ preventDefault: () => {} });
                                }}
                                className="px-3 py-2 bg-green-600 hover:bg-green-700 rounded-lg text-white text-sm font-semibold"
                              >
                                Set Win
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  setTradeOutcomeForm({ tradeId: trade.trade_id, outcome: 'loss' });
                                  handleTradeOutcomeUpdate({ preventDefault: () => {} });
                                }}
                                className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-white text-sm font-semibold"
                              >
                                Set Loss
                              </button>
                            </div>
                          </form>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;
